import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";

const STATUS_COLORS = {
  received: "bg-blue-500",
  in_progress: "bg-amber-500",
  completed: "bg-green-500",
  delivered: "bg-neutral-600",
};

export default function AdminCalendar() {
  const navigate = useNavigate();
  const [repairs, setRepairs] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    api.get("/admin/repairs").then((r) => setRepairs(r.data));
    api.get("/admin/reservations").then((r) => setReservations(r.data));
  }, []);

  const monthLabel = cursor.toLocaleDateString("ro-RO", { month: "long", year: "numeric" });
  const grid = useMemo(() => buildGrid(cursor), [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    repairs.forEach((r) => {
      const k = (r.created_at || "").slice(0, 10);
      (map[k] = map[k] || []).push({ type: "repair", id: r.id, label: r.scooter_model, customer: r.customer_name, status: r.status });
    });
    reservations.forEach((res) => {
      const dKey = res.preferred_date || (res.created_at || "").slice(0, 10);
      (map[dKey] = map[dKey] || []).push({ type: "reservation", id: res.id, label: res.scooter_model, customer: res.name, status: res.status });
    });
    return map;
  }, [repairs, reservations]);

  const prev = () => { const d = new Date(cursor); d.setMonth(d.getMonth() - 1); setCursor(d); };
  const next = () => { const d = new Date(cursor); d.setMonth(d.getMonth() + 1); setCursor(d); };
  const today = () => { const d = new Date(); d.setDate(1); setCursor(d); };

  const todayKey = new Date().toISOString().slice(0, 10);
  const selectedEvents = selectedDay ? eventsByDay[selectedDay] || [] : [];

  return (
    <div data-testid="admin-calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl uppercase">Calendar</h1>
          <p className="text-sm text-neutral-400 mt-1">Vezi reparatii si programari pe zile</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prev} data-testid="cal-prev" className="border border-neutral-800 hover:border-[#00E5FF] p-2"><ChevronLeft className="w-4 h-4" /></button>
          <div className="bg-neutral-950 border border-neutral-800 px-4 py-2 text-sm uppercase tracking-wider min-w-[180px] text-center capitalize">{monthLabel}</div>
          <button onClick={next} data-testid="cal-next" className="border border-neutral-800 hover:border-[#00E5FF] p-2"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={today} className="ml-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-[#00E5FF] px-3">Azi</button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2 text-[10px] uppercase tracking-widest text-neutral-500 text-center">
        {["Lu", "Ma", "Mi", "Jo", "Vi", "Sa", "Du"].map((d) => <div key={d}>{d}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {grid.map((cell, i) => {
          if (!cell) return <div key={i} className="aspect-square bg-neutral-950/40 border border-neutral-900" />;
          const k = cell.iso;
          const events = eventsByDay[k] || [];
          const isToday = k === todayKey;
          const isSelected = k === selectedDay;
          return (
            <button
              key={i}
              data-testid={`cal-day-${k}`}
              onClick={() => setSelectedDay(k)}
              className={`aspect-square bg-neutral-950 border p-1.5 text-left flex flex-col gap-1 transition-colors ${isSelected ? "border-[#00E5FF] ring-2 ring-[#00E5FF]/30" : "border-neutral-800 hover:border-[#00E5FF]/60"}`}
            >
              <div className={`text-xs ${isToday ? "text-[#00E5FF] font-bold" : "text-neutral-300"}`}>{cell.day}</div>
              <div className="flex flex-wrap gap-0.5 overflow-hidden">
                {events.slice(0, 4).map((ev, idx) => (
                  <span key={idx} className={`w-1.5 h-1.5 rounded-full ${STATUS_COLORS[ev.status] || "bg-[#00E5FF]"}`} />
                ))}
                {events.length > 4 && <span className="text-[9px] text-neutral-500">+{events.length - 4}</span>}
              </div>
              {events.length > 0 && (
                <div className="mt-auto text-[10px] text-neutral-500">{events.length} ev.</div>
              )}
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <div className="mt-8 bg-neutral-950 border border-neutral-800 p-6">
          <div className="flex items-center gap-2 mb-4">
            <CalendarIcon className="w-5 h-5 text-[#00E5FF]" />
            <h3 className="font-display text-xl uppercase">{new Date(selectedDay).toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h3>
            <span className="ml-auto text-xs text-neutral-500">{selectedEvents.length} evenimente</span>
          </div>
          {selectedEvents.length === 0 ? (
            <div className="text-sm text-neutral-500 text-center py-6">Nicio activitate</div>
          ) : (
            <div className="space-y-2">
              {selectedEvents.map((ev, i) => (
                <div
                  key={i}
                  onClick={() => navigate(ev.type === "repair" ? `/admin/reparatii` : `/admin/rezervari`)}
                  className="border border-neutral-800 hover:border-[#00E5FF] p-3 cursor-pointer flex items-center gap-3"
                >
                  <span className={`text-[10px] uppercase tracking-widest text-white px-2 py-0.5 ${ev.type === "repair" ? "bg-[#00E5FF]" : "bg-blue-500"}`}>{ev.type === "repair" ? "Reparatie" : "Rezervare"}</span>
                  <span className="font-display uppercase">{ev.label}</span>
                  <span className="text-sm text-neutral-400">· {ev.customer}</span>
                  <span className={`ml-auto w-2 h-2 rounded-full ${STATUS_COLORS[ev.status] || "bg-neutral-500"}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function buildGrid(monthDate) {
  const y = monthDate.getFullYear(), m = monthDate.getMonth();
  const first = new Date(y, m, 1);
  // ISO: Monday = 0 ... Sunday = 6
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dt = new Date(y, m, d);
    cells.push({ day: d, iso: `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}` });
  }
  while (cells.length % 7) cells.push(null);
  return cells;
}
